import { ref, onMounted, onUnmounted, computed, Ref } from "vue";

/**
 * Configuration options for network resilience
 */
export interface NetworkResilienceOptions {
  /** Maximum number of retry attempts for initial transaction */
  maxRetries?: number;
  /** Maximum consecutive polling failures before circuit breaking */
  maxConsecutiveFailures?: number;
  /** Transaction timeout in milliseconds */
  transactionTimeout?: number;
  /** Initial polling interval in milliseconds */
  initialPollingInterval?: number;
  /** Maximum polling interval in milliseconds (for exponential backoff) */
  maxPollingInterval?: number;
  /** Base delay for retry exponential backoff in milliseconds */
  retryBaseDelay?: number;
  /** Maximum retry delay in milliseconds */
  maxRetryDelay?: number;
  /** Exponential backoff multiplier for polling */
  pollingBackoffMultiplier?: number;
}

/**
 * Status types for payment transactions
 */
export type TransactionStatus =
  | "initializing"
  | "waiting"
  | "processing"
  | "success"
  | "errored"
  | "offline"
  | "aborting"
  | "aborted"
  | "terminated";

/**
 * Error context for better error messaging
 */
export interface ErrorContext {
  isNetworkError: boolean;
  retryCount: number;
  consecutiveFailures: number;
  errorMessage: string;
  originalError: any;
}

/**
 * Strategy 1: Network Status Detection
 * Strategy 2: Circuit Breaker Pattern
 * Strategy 3: Transaction Timeout Handling
 * Strategy 6: Better Error Messaging
 */
export function useNetworkResilience(options: NetworkResilienceOptions = {}) {
  const {
    maxRetries = 3,
    maxConsecutiveFailures = 500,
    transactionTimeout = 5 * 60 * 1000, // 5 minutes
  } = options;

  // Network state
  const isOnline = ref(navigator.onLine);
  const consecutiveFailures = ref(0);
  const transactionStartTime = ref<number | null>(null);

  /**
   * Strategy 1: Network Status Detection
   */
  const handleOnline = () => {
    console.log("Network connection restored");
    isOnline.value = true;
  };

  const handleOffline = () => {
    console.log("Network connection lost");
    isOnline.value = false;
  };

  /**
   * Strategy 6: Better Error Messaging
   * Analyzes error and provides context
   */
  const analyzeError = (error: any, retryCount: number): ErrorContext => {
    const isNetworkError =
      !navigator.onLine ||
      error.message?.includes("network") ||
      error.message?.includes("fetch") ||
      error.name === "NetworkError" ||
      error.name === "AbortError";

    let errorMessage = error.message || "An unknown error occurred";

    if (isNetworkError) {
      errorMessage = "Network error: Please check your connection.";
    }

    return {
      isNetworkError,
      retryCount,
      consecutiveFailures: consecutiveFailures.value,
      errorMessage,
      originalError: error,
    };
  };

  /**
   * Strategy 2: Circuit Breaker Pattern
   * Checks if circuit breaker should trip
   */
  const shouldCircuitBreak = (): boolean => {
    return consecutiveFailures.value >= maxConsecutiveFailures;
  };

  /**
   * Strategy 3: Transaction Timeout Handling
   * Checks if transaction has timed out
   */
  const hasTransactionTimedOut = (): boolean => {
    if (!transactionStartTime.value) return false;
    const elapsed = Date.now() - transactionStartTime.value;
    return elapsed > transactionTimeout;
  };

  /**
   * Start tracking transaction time
   */
  const startTransactionTimer = () => {
    transactionStartTime.value = Date.now();
  };

  /**
   * Reset failure counter (call on successful request)
   */
  const resetFailures = () => {
    consecutiveFailures.value = 0;
  };

  /**
   * Increment failure counter
   */
  const incrementFailures = () => {
    consecutiveFailures.value++;
  };

  /**
   * Get status message based on failures
   */
  const getStatusMessage = (baseMessage: string): string => {
    if (!isOnline.value) {
      return "No internet connection. Waiting for connection...";
    }

    if (shouldCircuitBreak()) {
      return "Too many network errors. Please check your connection and try again.";
    }

    if (consecutiveFailures.value >= 3) {
      return `Network issues detected. Retrying... (${consecutiveFailures.value}/${maxConsecutiveFailures})`;
    }

    return baseMessage;
  };

  // Set up online/offline listeners
  onMounted(() => {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
  });

  onUnmounted(() => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  });

  return {
    // State
    isOnline,
    consecutiveFailures,
    transactionStartTime,

    // Methods
    analyzeError,
    shouldCircuitBreak,
    hasTransactionTimedOut,
    startTransactionTimer,
    resetFailures,
    incrementFailures,
    getStatusMessage,

    // Constants
    maxRetries,
    maxConsecutiveFailures,
    transactionTimeout,
  };
}

/**
 * Strategy 4: Retry Logic for Initial Transaction
 * Strategy 5: AbortController for Request Cancellation (integrated)
 * Provides exponential backoff retry mechanism with request cancellation
 */
export function useRetryLogic(options: NetworkResilienceOptions = {}) {
  const {
    maxRetries = 3,
    retryBaseDelay = 2000,
    maxRetryDelay = 8000,
  } = options;

  const retryAbortController = ref<AbortController | null>(null);

  /**
   * Strategy 4: Exponential Backoff for Retries
   * Calculates delay for retry attempts
   */
  const calculateRetryDelay = (retryCount: number): number => {
    return Math.min(retryBaseDelay * Math.pow(2, retryCount), maxRetryDelay);
  };

  /**
   * Strategy 5: Create abort signal for retry requests
   */
  const createRetryAbortSignal = (): AbortSignal => {
    // Abort any existing retry request
    if (retryAbortController.value) {
      retryAbortController.value.abort();
    }

    retryAbortController.value = new AbortController();
    return retryAbortController.value.signal;
  };

  /**
   * Executes an async function with retry logic and request cancellation
   */
  const executeWithRetry = async <T>(
    fn: (signal: AbortSignal) => Promise<T>,
    onRetry?: (retryCount: number, delay: number) => void,
    retryCount = 0,
  ): Promise<T> => {
    // Strategy 5: Create abort signal for this attempt
    const signal = createRetryAbortSignal();

    try {
      return await fn(signal);
    } catch (error: any) {
      // Don't retry if this request was intentionally aborted
      if (error.name === "AbortError") {
        throw error;
      }

      const isNetworkError =
        !navigator.onLine ||
        error.message?.includes("network") ||
        error.message?.includes("fetch") ||
        error.name === "NetworkError";

      if (isNetworkError && retryCount < maxRetries) {
        const delay = calculateRetryDelay(retryCount);

        if (onRetry) {
          onRetry(retryCount + 1, delay);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
        return executeWithRetry(fn, onRetry, retryCount + 1);
      }

      throw error;
    }
  };

  /**
   * Cleanup - abort any in-flight retry requests
   */
  const cleanupRetry = () => {
    if (retryAbortController.value) {
      retryAbortController.value.abort();
      retryAbortController.value = null;
    }
  };

  onUnmounted(() => {
    cleanupRetry();
  });

  return {
    executeWithRetry,
    calculateRetryDelay,
    cleanupRetry,
    maxRetries,
  };
}

/**
 * Strategy 1: Exponential Backoff for Polling
 * Strategy 5: AbortController for Request Cancellation
 */
export function usePolling(options: NetworkResilienceOptions = {}) {
  const {
    initialPollingInterval = 2000,
    maxPollingInterval = 30000,
    pollingBackoffMultiplier = 1.5,
  } = options;

  const pollingInterval = ref<number | null>(null);
  const currentPollingInterval = ref(initialPollingInterval);
  const abortController = ref<AbortController | null>(null);

  /**
   * Strategy 1: Exponential Backoff for Polling
   * Calculates next polling interval with exponential backoff
   */
  const calculatePollingInterval = (failureCount: number): number => {
    return Math.min(
      initialPollingInterval * Math.pow(pollingBackoffMultiplier, failureCount),
      maxPollingInterval,
    );
  };

  /**
   * Updates polling interval based on failure count
   */
  const updatePollingInterval = (failureCount: number) => {
    currentPollingInterval.value = calculatePollingInterval(failureCount);
  };

  /**
   * Resets polling interval to initial value
   */
  const resetPollingInterval = () => {
    currentPollingInterval.value = initialPollingInterval;
  };

  /**
   * Strategy 5: AbortController for Request Cancellation
   * Creates a new AbortController and returns its signal
   */
  const createAbortSignal = (): AbortSignal => {
    // Abort any existing request
    if (abortController.value) {
      abortController.value.abort();
    }

    abortController.value = new AbortController();
    return abortController.value.signal;
  };

  /**
   * Starts polling with the current interval
   */
  const startPolling = (pollFn: () => void | Promise<void>) => {
    stopPolling(); // Clear any existing interval
    pollingInterval.value = window.setInterval(
      pollFn,
      currentPollingInterval.value,
    );
  };

  /**
   * Stops polling and aborts any in-flight requests
   */
  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }

    // Abort any in-flight request
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
  };

  /**
   * Check if an error is an abort error (should be ignored)
   */
  const isAbortError = (error: any): boolean => {
    return error.name === "AbortError";
  };

  // Clean up on unmount
  onUnmounted(() => {
    stopPolling();
  });

  return {
    // State
    currentPollingInterval,
    pollingInterval,

    // Methods
    startPolling,
    stopPolling,
    createAbortSignal,
    updatePollingInterval,
    resetPollingInterval,
    calculatePollingInterval,
    isAbortError,
  };
}

/**
 * Combined composable that uses all resilience strategies
 * This is a convenience wrapper for common use cases
 */
export function usePaymentResilience(options: NetworkResilienceOptions = {}) {
  const networkResilience = useNetworkResilience(options);
  const retryLogic = useRetryLogic(options);
  const polling = usePolling(options);

  return {
    ...networkResilience,
    ...retryLogic,
    ...polling,
  };
}
